import { withStyles } from '@material-ui/core/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Accordion, AccordionDetails, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import MuiAccordionSummary, { accordionSummaryClasses } from '@mui/material/AccordionSummary';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import { PropTypes } from 'prop-types';
import React, { useEffect, useState } from 'react';
import CustomPagination from '../../../common/components/CustomPagination';

const styles = (theme) => ({
  root: {
    width: '100%',
    margin: '10px 0',
    height: '400px',
    overflow: 'auto'
  },
  heading: {
    fontSize: '15px !important',
    fontWeight: '600 !important'
  },
  subheading: {
    fontSize: '14px !important'
  },
  accordionDetails: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column'
  },
  activityType: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1)
  },
  statusMessage: {
    display: 'flex !important',
    flexDirection: 'column !important',
    width: '100% !important',
    alignItems: 'center !important',
    marginTop: '15px !important'
  },
  divider: {
    margin: theme.spacing(2, 0)
  },
  selectable: {
    userSelect: 'text'
  }
});

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />} {...props} />
))(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
    transform: 'rotate(90deg)'
  },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1)
  },
  ...theme.applyStyles('dark', {
    backgroundColor: 'rgba(255, 255, 255, .05)'
  })
}));

const CopyableField = ({ label, value, fieldClasses }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    if (value) {
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    }
  };

  return (
    <Typography className={`${fieldClasses.subheading} ${fieldClasses.selectable}`}>
      {label}: {value}
      <Tooltip title={copied ? 'Copied!' : `Copy ${label}`}>
        <IconButton size="small" onClick={handleCopy}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Typography>
  );
};

CopyableField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  fieldClasses: PropTypes.object.isRequired
};

const PVSIngestionComponent = ({ data, classes, paginationData, handleSearch }) => {
  // Pagination
  const [serverPage, setServerPage] = useState(1);
  const rowsPerPage = 100;
  const totalPages = Math.ceil(paginationData?.total / rowsPerPage);

  useEffect(() => {
    setServerPage(paginationData?.page);
  }, [paginationData?.page]);

  const getActivityLabel = (type) => {
    let typeLabel = '';
    switch (type) {
      case 'ai_indexing':
        typeLabel = 'AI Indexing';
        break;
      case 'summary_generation':
        typeLabel = 'Summary Generation';
        break;
      case 'mcq_generation':
        typeLabel = 'MCQ Generation';
        break;
      case 'topics_generation':
        typeLabel = 'Topics Generation';
        break;
      case 'image_extraction':
        typeLabel = 'Image Extraction';
        break;
      default:
    }
    return typeLabel;
  };

  const changePage = (newPage) => {
    setServerPage(newPage);
    handleSearch({ paginationData: { page: newPage, limit: rowsPerPage } });
  };

  return (
    <>
      <div className={classes.root}>
        {data &&
          Array.isArray(data) &&
          data.length > 0 &&
          data?.map((course, index) => (
            <Accordion key={index} style={{ minHeight: '60px' }}>
              <AccordionSummary aria-controls={`panel${index}-content`} id={`panel${index}-header`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <div>
                    <CopyableField label="Course Id" value={course?.courseId} fieldClasses={classes} />
                    <CopyableField label="Manifest Id" value={course?.manifestId} fieldClasses={classes} />
                    <CopyableField label="Tracking Id" value={course?.trackingId} fieldClasses={classes} />
                  </div>
                  <div>
                    <Typography className={classes.heading}>Status: {course?.status?.code}</Typography>
                    <Typography className={classes.subheading}>Updated At: {course?.updatedAt}</Typography>
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <Typography
                  className={classes.heading}
                  style={{ display: 'flex', justifyContent: 'center', textDecoration: 'underline', padding: '10px' }}
                >
                  Ingestion Activities
                </Typography>
                {course?.activities?.map((activity, activityIndex) => (
                  <>
                    <div key={activityIndex}>
                      <Divider className={classes.heading}>
                        <Chip label={getActivityLabel(activity?.type)} size="medium" />
                      </Divider>
                      {activity?.statusMessages?.map((statusMessage, statusIndex) => (
                        <>
                          <hr width="1" size="20" style={{ margin: '0 auto' }} />
                          <div key={statusIndex} className={classes.statusMessage}>
                            <Typography className={classes.heading}>
                              Status: {statusMessage?.manifestProcessingStatusCode}
                            </Typography>
                            {statusMessage?.manifestProcessingStatusCode === 'CompletedWithErrors' && (
                              <Typography className={classes.subheading}>(Reason: {course?.status?.text})</Typography>
                            )}
                            <Typography className={classes.subheading}>
                              Creation Date: {new Date(statusMessage?.creationDt).toLocaleString()}
                            </Typography>
                          </div>
                        </>
                      ))}
                      {activityIndex < course?.activities.length - 1 ? (
                        <>
                          <hr width="1" size="20" style={{ margin: '0 auto' }} />
                          <div style={{ margin: '-14px', textAlign: 'center' }}>
                            <KeyboardArrowDownIcon />
                          </div>
                          <hr width="1" size="20" style={{ margin: '0 auto' }} />
                        </>
                      ) : (
                        <Divider />
                      )}
                    </div>
                  </>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
      </div>
      {data && Array.isArray(data) && data.length > 0 && (
        <CustomPagination
          serverPage={serverPage}
          totalPages={totalPages}
          paginationData={paginationData}
          rowsPerPage={rowsPerPage}
          changePage={changePage}
        />
      )}
    </>
  );
};

PVSIngestionComponent.propTypes = {
  data: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
  handleSearch: PropTypes.func.isRequired,
  paginationData: PropTypes.object.isRequired
};

export default withStyles(styles)(PVSIngestionComponent);
